import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, ArrowRight, Sparkles, Building2 } from 'lucide-react';
import { useLocation } from '@/contexts/LocationContext';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';

interface LocationPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const LocationPopup: React.FC<LocationPopupProps> = ({ isOpen, onClose }) => {
  const { selectedState, setSelectedState, setSelectedCenter, setSelectedCenterId, stateConfigs } = useLocation();
  const [tempState, setTempState] = useState<string>('');
  const [tempCenter, setTempCenter] = useState<string>('');
  const navigate = useNavigate();
  const location = useRouterLocation();

  const states = [
    { code: 'NSW', name: 'New South Wales', shortName: 'NSW' },
    { code: 'VIC', name: 'Victoria', shortName: 'VIC' },
    { code: 'QLD', name: 'Queensland', shortName: 'QLD' },
    { code: 'SA', name: 'South Australia', shortName: 'SA' },
    { code: 'WA', name: 'Western Australia', shortName: 'WA' },
    { code: 'TAS', name: 'Tasmania', shortName: 'TAS' },
    { code: 'NT', name: 'Northern Territory', shortName: 'NT' },
    { code: 'ACT', name: 'Australian Capital Territory', shortName: 'ACT' }
  ];

  const handleStateSelect = (stateCode: string) => {
    setTempState(stateCode);
    setTempCenter(''); // Reset center when state changes
  };

  const handleContinue = () => {
    if (!tempState) return;

    // Set the state in context
    setSelectedState(tempState);
    
    // Save to localStorage to prevent popup from showing again
    localStorage.setItem('schoolola_location_selected', 'true');
    localStorage.setItem('schoolola_selected_state', tempState);
    
    if (tempCenter) {
      const stateConfig = stateConfigs.find(sc => sc.code === tempState);
      const center = stateConfig?.centers.find(c => c.id === tempCenter);
      if (center) {
        // Update all center-related state in context and localStorage
        localStorage.setItem('schoolola_selected_center', center.slug);
        localStorage.setItem('schoolola_selected_center_id', center.uuid);
        setSelectedCenter(center.slug);
        setSelectedCenterId(center.uuid);
        
        // Only navigate to center page if on homepage, otherwise stay on current page
        if (location.pathname === '/') {
          navigate(`/centres/${center.slug}`);
        }
      }
    }
    
    onClose();
  };

  const handleFranchiseRedirect = () => {
    // Set the state in context
    setSelectedState(tempState);
    localStorage.setItem('schoolola_location_selected', 'true');
    localStorage.setItem('schoolola_selected_state', tempState);
    
    onClose();
    
    // Navigate to franchise page and scroll to master franchise section
    navigate('/franchise');
    setTimeout(() => {
      const masterFranchiseSection = document.getElementById('master-franchise');
      if (masterFranchiseSection) {
        masterFranchiseSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Get the state config for the temporarily selected state from database
  const currentStateForSelection = stateConfigs.find(sc => sc.code === tempState);

  return (
    <Dialog open={isOpen} onOpenChange={() => {}} modal>
      <DialogContent 
        className="max-w-lg mx-auto border-0 shadow-none z-[100] p-0 bg-transparent [&>button]:hidden" 
        onEscapeKeyDown={(e) => e.preventDefault()} 
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Modern Glassmorphism Container */}
        <div className="relative">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-accent/10 to-primary/30 rounded-3xl blur-xl"></div>
          
          {/* Main Content Card */}
          <div className="relative bg-white/90 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl p-8">
            {/* Decorative Elements */}
            <div className="absolute -top-2 -right-2 w-20 h-20 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-tr from-accent/20 to-primary/20 rounded-full blur-2xl"></div>
            
            {/* Header Section */}
            <div className="text-center space-y-6 mb-8">
              {/* Icon with Gradient Background */}
              <div className="relative inline-flex">
                <div className="absolute inset-0 bg-gradient-to-br from-primary to-accent rounded-2xl blur-lg opacity-30"></div>
                <div className="relative h-20 w-20 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center shadow-xl">
                  <MapPin className="h-10 w-10 text-white" />
                  <Sparkles className="absolute -top-1 -right-1 h-6 w-6 text-white/80" />
                </div>
              </div>
              
              {/* Modern Typography */}
              <div className="space-y-3">
                <h1 className="text-3xl font-heading font-bold bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                  Welcome to Schoolola!
                </h1>
                <p className="text-gray-600 text-lg leading-relaxed max-w-md mx-auto">
                  Let's personalise your experience by selecting your location
                </p>
              </div>
            </div>

            {/* Form Section */}
            <div className="space-y-6">
              {/* State Selection as Buttons */}
              <div className="space-y-4">
                <label className="block text-sm font-semibold text-gray-900 uppercase tracking-wide">
                  Select your state
                  <span className="text-primary ml-1">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {states.map((state) => (
                    <Button
                      key={state.code}
                      variant={tempState === state.code ? "default" : "outline"}
                      onClick={() => handleStateSelect(state.code)}
                      className={`h-12 rounded-xl transition-all duration-200 ${
                        tempState === state.code
                          ? 'bg-gradient-to-r from-primary to-accent text-white shadow-lg'
                          : 'bg-white/60 backdrop-blur-sm border-white/40 hover:border-primary/50 hover:bg-primary/5 hover:text-primary'
                      }`}
                    >
                      {state.shortName}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Center Selection or Franchise CTA - Animated Entrance */}
              {tempState && (
                <div className="space-y-3 animate-in slide-in-from-bottom-4 duration-300">
                  {currentStateForSelection && currentStateForSelection.centers.length > 0 ? (
                    <>
                      <label className="block text-sm font-semibold text-gray-900 uppercase tracking-wide">
                        Nearest centre
                        <span className="text-gray-500 ml-1 normal-case">(optional)</span>
                      </label>
                      <div className="relative">
                        <Select value={tempCenter} onValueChange={setTempCenter}>
                          <SelectTrigger className="w-full bg-white/60 backdrop-blur-sm border-white/40 rounded-2xl h-14 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 z-[101]">
                            <SelectValue placeholder="Choose your nearest centre" />
                          </SelectTrigger>
                          <SelectContent className="bg-white/95 backdrop-blur-xl border border-white/40 shadow-2xl rounded-2xl z-[102]">
                            {currentStateForSelection.centers.map((center: any) => (
                              <SelectItem 
                                key={center.id} 
                                value={center.id} 
                                className="hover:bg-primary/10 rounded-xl mx-2 my-1 transition-colors duration-200"
                              >
                                <span className="flex items-center gap-2">
                                  {center.name}
                                  {center.comingSoon && (
                                    <span className="inline-flex items-center rounded-full bg-accent/20 text-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                                      Coming Soon
                                    </span>
                                  )}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  ) : (
                    <div className="text-center space-y-4 p-6 bg-gradient-to-br from-accent/10 to-primary/10 rounded-2xl border border-white/30">
                      <Building2 className="h-12 w-12 text-primary mx-auto" />
                      <div className="space-y-2">
                        <h3 className="font-semibold text-gray-900">Coming Soon to {states.find(s => s.code === tempState)?.name}</h3>
                        <p className="text-sm text-gray-600">We're expanding! Learn about franchise opportunities in your area.</p>
                      </div>
                      <Button
                        onClick={handleFranchiseRedirect}
                        variant="outline"
                        className="w-full bg-white/60 backdrop-blur-sm border-primary/30 text-primary hover:bg-primary hover:text-white rounded-xl transition-all duration-300"
                      >
                        <Building2 className="mr-2 h-4 w-4" />
                        Explore Franchise Opportunities
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* CTA Button - Only show when centers exist */}
              {tempState && currentStateForSelection && currentStateForSelection.centers.length > 0 && (
                <div className="pt-4">
                  <Button 
                    onClick={handleContinue}
                    className="w-full bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 text-white rounded-2xl h-14 font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 group"
                  >
                    <span className="mr-2">Continue to Schoolola</span>
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </Button>
                </div>
              )}
            </div>

            {/* Footer Note */}
            <div className="text-center pt-6 mt-6 border-t border-gray-200/50">
              <p className="text-sm text-gray-500">
                You can change your location anytime in the header
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LocationPopup;